import React, { useState, useEffect, useRef } from 'react';
import CircularProgress from './CircularProgress';
import audioManager from '../../utils/audio';
import wakeLockManager from '../../utils/wakeLock';

const EMOMTimer = ({ rounds = null, intervalSec = 60, exercises = [], onComplete, onStop }) => {
  const openEnded = rounds == null;
  const totalSeconds = openEnded ? null : rounds * intervalSec;

  const [elapsed, setElapsed] = useState(0);
  const [currentRound, setCurrentRound] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const lastBeepSecondRef = useRef(-1);

  const secondsInCurrentInterval = elapsed % intervalSec;
  const secondsLeftInInterval = intervalSec - secondsInCurrentInterval;
  const progress = (secondsInCurrentInterval / intervalSec) * 100;
  const currentExercise = exercises && exercises.length > 0
    ? exercises[((currentRound || 1) - 1) % exercises.length]
    : 'EMOM';

  const getExerciseDisplay = (exercise) => {
    if (typeof exercise === 'string') return exercise;
    if (exercise?.targetReps) {
      return `${exercise.targetReps} ${exercise.name}`;
    }
    return exercise?.name || 'Exercise';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(() => {
        setElapsed((prev) => {
          const newElapsed = prev + 1;
          const round = Math.floor(newElapsed / intervalSec) + 1;
          setCurrentRound(round);

          const secondInInterval = newElapsed % intervalSec;
          if (secondInInterval === 0 && newElapsed > 0 && lastBeepSecondRef.current !== newElapsed) {
            audioManager.transitionBeep();
            audioManager.speak(`Round ${round}`);
            lastBeepSecondRef.current = newElapsed;
          }

          // Fixed-round mode: count down to completion
          if (!openEnded && totalSeconds != null) {
            const remaining = totalSeconds - newElapsed;
            if (remaining <= 3 && remaining > 0) {
              audioManager.countdownBeep(remaining);
            }
            if (remaining <= 0) {
              clearInterval(intervalRef.current);
              setIsRunning(false);
              audioManager.completionSound();
              audioManager.speak('Workout complete!');
              wakeLockManager.release();
              if (onComplete) onComplete();
              return newElapsed;
            }
          }

          return newElapsed;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, isPaused, rounds, intervalSec, totalSeconds, openEnded, onComplete]);

  const handleStart = () => {
    audioManager.init();
    wakeLockManager.request();

    setCountdown(3);
    audioManager.speak('3');

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return null;
        const next = prev - 1;
        if (next > 0) audioManager.speak(String(next));
        else if (next === 0) audioManager.speak('Go!');
        if (next < 0) {
          clearInterval(countdownRef.current);
          setCountdown(null);
          setIsRunning(true);
          setCurrentRound(1);
          return null;
        }
        return next;
      });
    }, 1000);
  };

  const handleToggle = () => {
    if (countdown !== null) return;
    if (!isRunning) {
      handleStart();
    } else if (isPaused) {
      setIsPaused(false);
      audioManager.speak('Go');
    } else {
      setIsPaused(true);
      audioManager.speak('Paused');
    }
  };

  const handleFinish = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsRunning(false);
    setIsPaused(false);
    wakeLockManager.release();
    audioManager.completionSound();
    audioManager.speak('Finished!');
    if (onComplete) onComplete(elapsed);
  };

  const handleStop = () => {
    if (window.confirm('Are you sure you want to stop the workout?')) {
      if (onStop) onStop();
    }
  };

  // Countdown phase
  if (countdown !== null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div
          key={countdown}
          className="text-gray-900 dark:text-white font-black tabular-nums animate-ping-once"
          style={{
            fontSize: 'clamp(15rem, 40vw, 30rem)',
            animation: 'scaleOut 1s ease-out'
          }}
        >
          {countdown === 0 ? 'GO' : countdown}
        </div>
        <style>{`
          @keyframes scaleOut {
            0% { transform: scale(0.5); opacity: 1; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1.15); opacity: 0.3; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 px-4">
      {/* Exercise name at top */}
      <div className="text-center mb-6">
        <div className="text-3xl font-bold text-gray-900 dark:text-white uppercase tracking-wide">
          {isRunning
            ? getExerciseDisplay(currentExercise)
            : openEnded
              ? 'OPEN EMOM'
              : `${rounds} RND EMOM`
          }
        </div>
        {isRunning && openEnded && (
          <div className="text-lg text-gray-500 dark:text-gray-400 mt-1">
            {formatTime(elapsed)}
          </div>
        )}
      </div>

      {/* Circular Timer */}
      <div className="relative">
        <CircularProgress progress={progress} size={320} strokeWidth={16} color="#6366f1">
          <div className="flex flex-col items-center relative">
            {!isRunning ? (
              <button
                onClick={handleToggle}
                className="w-32 h-32 rounded-full bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center shadow-xl transition-transform active:scale-95"
              >
                <svg className="w-16 h-16 text-white ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </button>
            ) : (
              <>
                <div className="text-8xl font-black tabular-nums leading-none text-gray-900 dark:text-white">
                  {secondsLeftInInterval}
                </div>
                <div className="absolute bottom-[-2.5rem] left-1/2 transform -translate-x-1/2 text-xl text-gray-500 dark:text-gray-400">
                  {isPaused ? 'PAUSED' : 'seconds'}
                </div>
              </>
            )}
          </div>
        </CircularProgress>
      </div>

      {/* Round counter */}
      <div className="my-8 text-center">
        <div className="text-5xl font-black text-gray-900 dark:text-white">
          {currentRound}
          {!openEnded && <span className="text-2xl text-gray-400 dark:text-gray-600">/{rounds}</span>}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {openEnded ? 'ROUNDS' : 'ROUND'}
        </div>
      </div>

      {/* Finish button for open-ended mode */}
      {isRunning && openEnded && !isPaused && (
        <button
          onClick={handleFinish}
          className="mb-6 px-10 py-3 bg-green-600 hover:bg-green-700 text-white rounded-full text-lg font-semibold uppercase tracking-wide shadow-lg transition-colors"
        >
          Finish
        </button>
      )}

      {/* Control buttons */}
      {isRunning && (
        <div className="flex gap-6">
          <button
            onClick={handleStop}
            className="w-16 h-16 rounded-full bg-gray-700 dark:bg-gray-600 hover:bg-gray-800 dark:hover:bg-gray-500 flex items-center justify-center shadow-lg transition-transform active:scale-95"
          >
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <line x1="8" y1="8" x2="16" y2="16" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <button
            onClick={handleToggle}
            className="w-16 h-16 rounded-full bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center shadow-lg transition-transform active:scale-95"
          >
            {isPaused ? (
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default EMOMTimer;
